import '../styles/GradeEdit.css';

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';

import {
  ApiClient,
  Button,
  CheckAuthorization,
  CheckIfLoggedIn,
  Input
} from '../components';
import USERROLES from '../constants/UserRoles';
import { isValidDate, isValidNumber } from '../helpers/validator';

export function GradeEdit({ setBreadcrumbLinks }) {
  let { addToast } = useToasts();
  const navigate = useNavigate();
  let { userId, semesterId, subjectId, gradeId } = useParams();

  const [error, setError] = useState({
    grade: '',
    weight: '',
    description: '',
    written_at: '',
    justification: ''
  });
  const [grade, setGrade] = useState({
    grade: 0,
    weight: 0,
    description: '',
    written_at: '',
    justification: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'AMP | Note editieren';
    Promise.all([
      ApiClient.get(`users/${userId}`),
      ApiClient.get(`users/${userId}/semesters`),
      ApiClient.get(
        `users/${userId}/semesters/${semesterId}/subjects/${subjectId}`
      ),
      ApiClient.get(`grades/${gradeId}`)
    ])
      .then(([user, { semesters }, subject, grade]) => {
        const semester = semesters.find(semester => semester.id == semesterId);
        setBreadcrumbLinks([
          {
            id: 1,
            label: `${user?.firstname} ${user?.lastname}`,
            path: `/users/${userId}/semesters`
          },
          {
            id: 2,
            label: 'Alle Semester',
            path: `/users/${userId}/semesters`
          },
          {
            id: 3,
            label: semester?.index,
            path: `/users/${userId}/semesters/${semesterId}/subjects`
          },
          {
            id: 4,
            label: 'Fächer',
            path: `/users/${userId}/semesters/${semesterId}/subjects`
          },
          {
            id: 5,
            label: subject?.subject?.name,
            // eslint-disable-next-line max-len
            path: `/users/${userId}/semesters/${semesterId}/subjects/${subjectId}/grades`
          },
          {
            id: 6,
            label: grade?.grade,
            // eslint-disable-next-line max-len
            path: `/users/${userId}/semesters/${semesterId}/subjects/${subjectId}/grades`
          },
          {
            id: 7,
            label: 'Bearbeiten',
            // eslint-disable-next-line max-len
            path: `/users/${userId}/semesters/${semesterId}/subjects/${subjectId}/grades/edit`
          }
        ]);
        setGrade(grade);
      })
      .catch(() =>
        addToast('Etwas ist schief gelaufen!', { appearance: 'error' })
      );
  }, [gradeId, addToast, userId, semesterId, subjectId, setBreadcrumbLinks]);

  function onInputChange(field, value) {
    setGrade({ ...grade, [field]: value });
    setError({ ...error, [field]: '' });
  }

  const validateInputFields = grade => {
    var checkInput =
      grade.grade && grade.weight && grade.description && grade.written_at;
    return grade.grade < 4 && checkInput ? !!grade.justification : checkInput;
  };

  function handleSubmitClicked() {
    if (!validateInputFields(grade)) {
      return setError({
        ...error,
        grade: !grade.grade ? 'Bitte Note eingeben!' : '',
        weight: !grade.weight ? 'Bitte Gewicht eingeben!' : '',
        description: !grade.description ? 'Bitte Beschreibung eingeben!' : '',
        written_at: !grade.written_at
          ? 'Bitte eingeben, wann die Prüfung geschrieben wurde'
          : '',
        justification: !grade.justification
          ? 'Bitte Rechtfertigung eingeben'
          : ''
      });
    }
    if (!isValidNumber(grade.grade)) {
      return setError({
        ...error,
        grade: 'Bitte geben Sie eine Zahl an!'
      });
    } else if (
      Number.parseFloat(grade.grade) < 1 ||
      Number.parseFloat(grade.grade) > 6
    ) {
      return setError({
        ...error,
        grade: 'Bitte geben Sie eine Zahl zwischen 1 und 6 an!'
      });
    }
    if (!isValidNumber(Number.parseFloat(grade.weight))) {
      return setError({
        ...error,
        weight: 'Bitte geben Sie eine Zahl an!'
      });
    } else if (
      Number.parseFloat(grade.weight) < 0 ||
      Number.parseFloat(grade.weight) > 1
    ) {
      return setError({
        ...error,
        weight: 'Bitte geben Sie eine Zahl zwischen 0 und 1 an!'
      });
    }
    if (grade.description.trim().length > 100) {
      return setError({
        ...error,
        description: 'Die maximale Länge der Beschreibung beträgt 100 Zeichen!'
      });
    }

    if (!isValidDate(grade.written_at)) {
      return setError({
        ...error,
        written_at: 'Bitte geben Sie ein valides Datum an!'
      });
    }

    if (grade.justification.trim().length > 100) {
      return setError({
        ...error,
        justification:
          'Die maximale Länge der Rechtfertigung beträgt 100 Zeichen'
      });
    }

    if (Date.now() < new Date(grade.written_at).getTime()) {
      return setError({
        ...error,
        written_at: 'Das Datum darf nicht in der Zukunft liegen!'
      });
    }

    setGrade({
      ...grade,
      description: grade.description.trim()
    });
    setLoading(true);
    ApiClient.patch(`grades/${gradeId}`, {
      grade: grade.grade,
      weight: grade.weight,
      description: grade.description,
      written_at: grade.written_at,
      justification: grade.justification
    })
      .then(() => {
        addToast('Erfolgreich gespeichert!', { appearance: 'success' });
        navigate(
          // eslint-disable-next-line max-len
          `/users/${userId}/semesters/${semesterId}/subjects/${subjectId}/grades`
        );
      })
      .catch(() =>
        addToast('Etwas ist schief gelaufen!', { appearance: 'error' })
      );
  }

  return (
    <CheckIfLoggedIn>
      <CheckAuthorization
        roles={[
          USERROLES.ADMIN,
          USERROLES.APPRENTICE_LEADER,
          USERROLES.INSTRUCTOR,
          USERROLES.APPRENTICE
        ]}
        shouldNavigate={false}
      >
        <div className='grade-edit container'>
          <div className='content'>
            <h1 className='title'>Note bearbeiten</h1>
            <div className='grade-edit form'>
              <Input
                error={error.grade}
                placeholder='Note'
                type='number'
                value={grade.grade}
                onChange={event => onInputChange('grade', event.target.value)}
                required
              />
            </div>
            {grade.grade < 4 ? (
              <div className='grade-edit form'>
                <Input
                  textarea
                  maxLength={100}
                  error={error.justification}
                  placeholder='Rechtfertigung'
                  type='text'
                  value={grade.justification}
                  onChange={event =>
                    onInputChange('justification', event.target.value)
                  }
                  required
                />
              </div>
            ) : (
              ''
            )}
            <div className='grade-edit form'>
              <Input
                error={error.weight}
                placeholder='Gewichtung'
                type='number'
                value={grade.weight}
                onChange={event => onInputChange('weight', event.target.value)}
                required
              />
            </div>
            <div className='grade-edit form'>
              <Input
                error={error.description}
                placeholder='Beschreibung'
                type='text'
                value={grade.description}
                maxLength={100}
                onChange={event =>
                  onInputChange('description', event.target.value)
                }
                required
              />
            </div>
            <div className='grade-edit form'>
              <Input
                error={error.written_at}
                placeholder='Geschrieben am'
                type='date'
                value={grade.written_at}
                onChange={event =>
                  onInputChange('written_at', event.target.value)
                }
                required
              />
            </div>
            <div className='button-group'>
              <Button
                text='Speichern'
                disabled={loading}
                loading={loading}
                className='hero'
                onClick={handleSubmitClicked}
              />
              <Button
                text='Abbrechen'
                disabled={false}
                loading={false}
                className=''
                onClick={() =>
                  navigate(
                    // eslint-disable-next-line max-len
                    `/users/${userId}/semesters/${semesterId}/subjects/${subjectId}/grades`
                  )
                }
              />
            </div>
          </div>
        </div>
      </CheckAuthorization>
    </CheckIfLoggedIn>
  );
}
